import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../../utils';

export interface SplitViewProps extends HTMLAttributes<HTMLDivElement> {
  /** Content for the left (primary) panel */
  left: ReactNode;
  /** Content for the right panel. When null, left panel takes full width. */
  right: ReactNode | null;
  /** Initial split percentage (0-100). Default: 50 */
  defaultSplit?: number;
  /** Minimum left panel width in pixels. Default: 200 */
  minLeft?: number;
  /** Minimum right panel width in pixels. Default: 200 */
  minRight?: number;
  /** Callback fired during drag with new split percentage */
  onResize?: (split: number) => void;
  /** localStorage key for persisting split position across renders */
  storageKey?: string;
}

function readStoredSplit(storageKey: string | undefined, defaultSplit: number): number {
  if (!storageKey) return defaultSplit;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed > 0 && parsed < 100) {
        return parsed;
      }
    }
  } catch {
    // localStorage unavailable (SSR, privacy mode)
  }
  return defaultSplit;
}

function writeStoredSplit(storageKey: string | undefined, split: number): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, String(split));
  } catch {
    // localStorage unavailable
  }
}

/**
 * SplitView - Resizable two-column layout component
 *
 * Renders a left panel and an optional right panel separated by a draggable handle.
 * When `right` is null, the left panel expands to full width and the handle is hidden.
 * Split position can be persisted to localStorage via the `storageKey` prop.
 */
export const SplitView = forwardRef<HTMLDivElement, SplitViewProps>(
  (
    {
      className,
      left,
      right,
      defaultSplit = 50,
      minLeft = 200,
      minRight = 200,
      onResize,
      storageKey,
      ...props
    },
    ref
  ) => {
    const [split, setSplit] = useState<number>(() =>
      readStoredSplit(storageKey, defaultSplit)
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);

    // Merge external ref with internal containerRef
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );

    const handlePointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        isDraggingRef.current = true;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      },
      []
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current) return;
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const containerWidth = rect.width;
        if (containerWidth === 0) return;

        const rawSplit = ((e.clientX - rect.left) / containerWidth) * 100;

        // Clamp to respect minLeft and minRight in pixel terms
        const minLeftPct = (minLeft / containerWidth) * 100;
        const minRightPct = (minRight / containerWidth) * 100;
        const clampedSplit = Math.min(
          Math.max(rawSplit, minLeftPct),
          100 - minRightPct
        );

        setSplit(clampedSplit);
        onResize?.(clampedSplit);
      },
      [minLeft, minRight, onResize]
    );

    const handlePointerUp = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        writeStoredSplit(storageKey, split);
      },
      [storageKey, split]
    );

    // Persist on split changes that end a drag (via storageKey)
    useEffect(() => {
      if (!isDraggingRef.current) {
        writeStoredSplit(storageKey, split);
      }
    }, [split, storageKey]);

    const hasRight = right !== null;

    return (
      <div
        ref={setRefs}
        className={cn('relative flex h-full w-full overflow-hidden', className)}
        onPointerMove={hasRight ? handlePointerMove : undefined}
        onPointerUp={hasRight ? handlePointerUp : undefined}
        {...props}
      >
        {/* Left panel */}
        <div
          className={cn(
            'flex-shrink-0 overflow-auto',
            'transition-[width] duration-150 ease-out',
            hasRight ? '' : 'w-full'
          )}
          style={hasRight ? { width: `${split}%` } : undefined}
        >
          {left}
        </div>

        {/* Drag handle — only rendered when right panel is present */}
        {hasRight && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panels"
            className={cn(
              'relative z-10 flex w-1 flex-shrink-0 cursor-col-resize select-none',
              'bg-slate-200 dark:bg-slate-700',
              'hover:bg-slate-300 dark:hover:bg-slate-600',
              'transition-colors duration-150',
              'items-center justify-center'
            )}
            onPointerDown={handlePointerDown}
          >
            {/* Grip dots */}
            <div className="flex flex-col items-center gap-[3px] pointer-events-none">
              <span className="block h-[3px] w-[3px] rounded-full bg-slate-400 dark:bg-slate-500" />
              <span className="block h-[3px] w-[3px] rounded-full bg-slate-400 dark:bg-slate-500" />
              <span className="block h-[3px] w-[3px] rounded-full bg-slate-400 dark:bg-slate-500" />
            </div>
          </div>
        )}

        {/* Right panel */}
        {hasRight && (
          <div
            className="flex-1 overflow-auto transition-[flex] duration-150 ease-out"
          >
            {right}
          </div>
        )}
      </div>
    );
  }
);

SplitView.displayName = 'SplitView';
